import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Briefcase, CalendarDays, Download, FileText, Loader2, Mail,
  MapPin, Pencil, Phone, Plus, StickyNote, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, API_URL, errMsg } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtSize, fmtTime, initials, timeAgo } from "@/lib/format";
import { ConfirmDialog, EmptyState, Loading, PageHeader, StatusBadge } from "@/components/common";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ClientFormDialog } from "./Clients";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [deletingNote, setDeletingNote] = useState(null);

  const load = useCallback(() => {
    api.get(`/clients/${id}/summary`)
      .then(({ data }) => setSummary(data))
      .catch(() => setError(true));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/clients/${id}/notes`, { text: noteText.trim() });
      setNoteText("");
      toast.success("Note added");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSavingNote(false);
    }
  };

  const removeNote = async () => {
    try {
      await api.delete(`/clients/${id}/notes/${deletingNote.id}`);
      toast.success("Note deleted");
      setDeletingNote(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (error) {
    return (
      <div data-testid="client-not-found">
        <EmptyState icon={FileText} title="Client not found" description="This client may have been removed."
          action={<Button onClick={() => navigate("/dashboard/clients")} className="bg-navy text-ivory rounded-none">Back to Clients</Button>} />
      </div>
    );
  }
  if (!summary) return <Loading label="Loading client" />;

  const { client, cases, documents, appointments, activity } = summary;

  return (
    <div data-testid="client-detail-page">
      <button onClick={() => navigate("/dashboard/clients")} data-testid="back-to-clients"
        className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-navy transition-colors mb-6">
        <ArrowLeft className="h-4 w-4" /> All Clients
      </button>

      <div className="bg-white border border-border shadow-card p-6 sm:p-8 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          <span className="h-16 w-16 bg-navy text-gold font-serif text-xl font-semibold flex items-center justify-center shrink-0">
            {initials(client.name)}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-serif text-3xl font-semibold text-navy" data-testid="client-name">{client.name}</h1>
              <StatusBadge status={client.status} />
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-sm text-muted-foreground">
              {client.email && <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-gold-700" />{client.email}</span>}
              {client.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-gold-700" />{client.phone}</span>}
              {client.address && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-gold-700" />{client.address}</span>}
            </div>
          </div>
          <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-client-btn" className="rounded-none shrink-0">
            <Pencil className="h-4 w-4 mr-2" /> Edit
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" data-testid="client-tabs">
        <TabsList className="bg-transparent border-b border-border rounded-none w-full justify-start overflow-x-auto h-auto p-0 gap-1">
          {["overview", "cases", "documents", "appointments", "notes", "activity"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`tab-${t}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:text-navy data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground capitalize">
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid md:grid-cols-3 gap-5">
            <div className="bg-white border border-border p-6">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">Details</h3>
              <dl className="space-y-3 text-sm">
                {[["Case type", client.case_type], ["Date of birth", fmtDate(client.dob)], ["Client since", fmtDate(client.created_at)]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd className="font-medium text-navy text-right">{v || "—"}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="bg-white border border-border p-6">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">Matters</h3>
              <div className="grid grid-cols-3 gap-2 text-center">
                {[[cases.length, "Cases"], [documents.length, "Docs"], [appointments.length, "Appts"]].map(([v, l]) => (
                  <div key={l} className="bg-muted/60 py-4">
                    <p className="font-serif text-2xl font-semibold text-navy">{v}</p>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{l}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-border p-6">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">Latest Note</h3>
              {client.notes?.length ? (
                <p className="text-sm text-foreground leading-relaxed">{client.notes[client.notes.length - 1].text}</p>
              ) : (
                <p className="text-sm text-muted-foreground">No notes yet.</p>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="cases" className="mt-6">
          {cases.length === 0 ? (
            <EmptyState icon={Briefcase} title="No cases" description="This client has no cases on file."
              action={<Link to="/dashboard/cases?new=1"><Button className="bg-navy text-ivory rounded-none" data-testid="client-tab-add-case">Open a Case</Button></Link>} />
          ) : (
            <div className="bg-white border border-border divide-y divide-border">
              {cases.map((c) => (
                <Link to="/dashboard/cases" key={c.id} className="flex flex-col sm:flex-row sm:items-center gap-3 px-6 py-4 hover:bg-muted/50 transition-colors" data-testid={`client-case-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-navy text-sm">{c.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{c.case_number} · {c.practice_area}</p>
                  </div>
                  <StatusBadge status={c.status} />
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          {documents.length === 0 ? (
            <EmptyState icon={FileText} title="No documents" description="Upload client documents from the Documents workspace."
              action={<Link to="/dashboard/documents"><Button className="bg-navy text-ivory rounded-none" data-testid="client-tab-upload-doc">Go to Documents</Button></Link>} />
          ) : (
            <div className="bg-white border border-border divide-y divide-border">
              {documents.map((d) => (
                <div key={d.id} className="flex items-center gap-4 px-6 py-4" data-testid={`client-doc-${d.id}`}>
                  <FileText className="h-5 w-5 text-gold-700 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-navy text-sm truncate">{d.name}</p>
                    <p className="text-xs text-muted-foreground">{d.category} · {fmtSize(d.size)} · {fmtDate(d.uploaded_at)}</p>
                  </div>
                  <a href={`${API_URL}/documents/${d.id}/download`} data-testid={`client-doc-download-${d.id}`}
                    className="p-2 text-muted-foreground hover:text-navy hover:bg-muted transition-colors" aria-label="Download">
                    <Download className="h-4 w-4" />
                  </a>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="appointments" className="mt-6">
          {appointments.length === 0 ? (
            <EmptyState icon={CalendarDays} title="No appointments" description="No meetings scheduled with this client."
              action={<Link to="/dashboard/appointments?new=1"><Button className="bg-navy text-ivory rounded-none" data-testid="client-tab-schedule">Schedule</Button></Link>} />
          ) : (
            <div className="bg-white border border-border divide-y divide-border">
              {appointments.map((a) => (
                <div key={a.id} className="flex flex-col sm:flex-row sm:items-center gap-3 px-6 py-4" data-testid={`client-appt-${a.id}`}>
                  <div className="flex-1">
                    <p className="font-semibold text-navy text-sm">{a.type}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{fmtDate(a.date)} at {fmtTime(a.time)} · {a.location}</p>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="notes" className="mt-6">
          <form onSubmit={addNote} className="bg-white border border-border p-6 mb-5" data-testid="note-form">
            <label className="text-xs font-semibold uppercase tracking-widest text-navy-700">Add a note</label>
            <textarea rows={3} value={noteText} onChange={(e) => setNoteText(e.target.value)}
              data-testid="note-input" placeholder="Case strategy, client preferences, follow-ups…"
              className="mt-2 w-full border border-border focus:border-gold px-3 py-2.5 text-sm outline-none resize-none" />
            <Button type="submit" disabled={savingNote || !noteText.trim()} data-testid="note-submit"
              className="mt-3 bg-navy text-ivory hover:bg-navy-800 rounded-none text-xs uppercase tracking-wider font-semibold">
              {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1.5" /> Add Note</>}
            </Button>
          </form>
          {client.notes?.length === 0 ? (
            <EmptyState icon={StickyNote} title="No notes yet" description="Notes are private to your workspace." testId="notes-empty" />
          ) : (
            <div className="space-y-3">
              {[...client.notes].reverse().map((n) => (
                <div key={n.id} className="bg-white border border-border p-5 flex gap-4" data-testid={`note-${n.id}`}>
                  <StickyNote className="h-4 w-4 text-gold-700 mt-1 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-foreground leading-relaxed">{n.text}</p>
                    <p className="text-xs text-muted-foreground mt-2">{n.author} · {timeAgo(n.created_at)}</p>
                  </div>
                  <button onClick={() => setDeletingNote(n)} data-testid={`note-delete-${n.id}`}
                    className="p-1.5 text-muted-foreground hover:text-red-600 transition-colors self-start" aria-label="Delete note">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="activity" className="mt-6">
          {activity.length === 0 ? (
            <EmptyState icon={CalendarDays} title="No activity" description="Actions involving this client will appear here." testId="activity-empty" />
          ) : (
            <div className="bg-white border border-border p-6">
              <div className="border-l-2 border-border space-y-6">
                {activity.map((a) => (
                  <div key={a.id} className="relative pl-6" data-testid={`client-activity-${a.id}`}>
                    <span className="absolute -left-[5px] top-1.5 h-2 w-2 bg-gold" />
                    <p className="text-sm text-navy">{a.text}</p>
                    <p className="text-xs text-muted-foreground mt-1">{fmtDateTime(a.created_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ClientFormDialog open={editOpen} onOpenChange={setEditOpen} client={client} onSaved={load} />
      <ConfirmDialog open={!!deletingNote} onOpenChange={() => setDeletingNote(null)}
        title="Delete this note?" description="The note will be permanently removed from this client's record."
        confirmLabel="Delete Note" onConfirm={removeNote} />
    </div>
  );
}
